import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { buildEntityColumns, entityDisplayNameFor } from '@/composables/entityColumns';
import { areaEntity } from '@/navigation/areas';
import { recordTitle } from '@/composables/recordTitle';
import { recordHeadingText, type RecordHeadingText } from '@/composables/recordLayout/recordHeading';
import { useRecordLayout } from '@/composables/recordLayout/useRecordLayout';

/** The record noun a heading names, for example "Player" in "Player 1". */
export function recordNounFor(entityId: string): string {
    return areaEntity(entityId)?.noun ?? entityDisplayNameFor(entityId);
}

/**
 * A record's top line and title for a peek's header: the side peek, the center
 * peek and the split pane. They read the person's layout from the same shared
 * copy the record page and Customize use, so a change in Customize shows in
 * the header at once, and the peek reads the same as the page.
 *
 * Until the record has loaded there is no top line, and the title is the
 * fallback, such as "Account 55".
 */
export function useRecordHeading(
    entityId: MaybeRefOrGetter<string>,
    recordId: MaybeRefOrGetter<number | string | null | undefined>,
    row: MaybeRefOrGetter<Record<string, unknown> | null | undefined>,
) {
    const columns = computed(() => buildEntityColumns(toValue(entityId)));
    const layoutState = useRecordLayout(entityId, columns);
    const noun = computed(() => recordNounFor(toValue(entityId)));

    return computed<RecordHeadingText>(() => {
        const record = toValue(row) ?? null;
        const id = toValue(recordId) ?? '';
        if (record === null) {
            return { topLine: '', title: recordTitle(null, noun.value, id) };
        }
        return recordHeadingText(layoutState.shown.value, columns.value, record, noun.value, id);
    });
}
